// 通用弹窗控制器：负责内容填充、开关、滚动锁定与焦点管理
const VISIBLE_CLASS = "visible";

// 弹窗内参与 Tab 焦点锁定的元素范围
const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** 弹窗可填充的内容 */
export interface ModalOpenOptions {
	/** 标题（纯文本） */
	title?: string;
	/** 标题左侧的序号角标（纯文本） */
	index?: string;
	/** 正文 HTML，可复用站点富文本样式；内容需来自可信来源 */
	content?: string;
	/**
	 * 底部操作链接，不传或传 null 则隐藏
	 * 文案缺省用视图渲染的 linkText，可在 text 中覆盖
	 */
	link?: { href: string; text?: string } | null;
	/** 关闭后焦点归位的元素，默认取打开瞬间的焦点元素 */
	trigger?: HTMLElement | null;
}

/** 弹窗实例 */
export interface ModalInstance {
	/** 填充内容并展示 */
	open(options?: ModalOpenOptions): void;
	/** 关闭并恢复页面状态 */
	close(): void;
	/** 是否处于展示状态 */
	isOpen(): boolean;
}

// 同时打开的弹窗数量，避免多个弹窗时提前解除页面滚动锁定
let openedModalCount = 0;

function lockBodyScroll() {
	openedModalCount += 1;
	document.body.style.overflow = "hidden";
}

function unlockBodyScroll() {
	openedModalCount = Math.max(0, openedModalCount - 1);
	if (openedModalCount === 0) {
		document.body.style.overflow = "";
	}
}

/** 初始化指定标识的弹窗，页面中不存在对应结构时返回 null */
export function initModal(id: string): ModalInstance | null {
	const overlay = document.querySelector<HTMLElement>(`[data-modal="${id}"]`);
	if (!overlay) return null;

	const titleEl = overlay.querySelector<HTMLElement>("[data-modal-title]");
	const bodyEl = overlay.querySelector<HTMLElement>("[data-modal-body]");
	const closeBtn =
		overlay.querySelector<HTMLButtonElement>("[data-modal-close]");
	if (!titleEl || !bodyEl || !closeBtn) return null;

	// 序号角标与操作链接为可选项，由视图按需渲染
	const indexEl = overlay.querySelector<HTMLElement>("[data-modal-index]");
	const linkEl = overlay.querySelector<HTMLAnchorElement>("[data-modal-link]");

	let trigger: HTMLElement | null = null;
	let opened = false;

	const open = (options: ModalOpenOptions = {}) => {
		if (opened) return;
		trigger = options.trigger ?? (document.activeElement as HTMLElement | null);

		titleEl.textContent = options.title || "";
		bodyEl.innerHTML = options.content || "";
		if (indexEl) indexEl.textContent = options.index || "";

		if (linkEl) {
			const href = options.link?.href;
			// 文案优先取调用方传入值，其次用视图渲染的默认文案，两者皆空则不展示链接
			const text =
				options.link?.text?.trim() || linkEl.textContent?.trim() || "";
			if (href && text) {
				linkEl.href = href;
				linkEl.textContent = text;
				linkEl.hidden = false;
			} else {
				linkEl.removeAttribute("href");
				linkEl.hidden = true;
			}
		}

		bodyEl.scrollTop = 0;
		overlay.classList.add(VISIBLE_CLASS);
		lockBodyScroll();
		opened = true;
		closeBtn.focus();
	};

	const close = () => {
		if (!opened) return;
		overlay.classList.remove(VISIBLE_CLASS);
		unlockBodyScroll();
		opened = false;
		trigger?.focus();
		trigger = null;
	};

	closeBtn.addEventListener("click", close);
	overlay.addEventListener("click", (e) => {
		if (e.target === overlay) close();
	});
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && opened) close();
	});

	// 弹窗内可聚焦元素：随内容动态统计，正文里的链接/按钮同样纳入焦点锁定
	const focusables = () =>
		Array.from(
			overlay.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
		).filter((el) => el.getClientRects().length > 0);

	// Tab 焦点锁定在弹窗内，避免焦点跑到页面背景
	overlay.addEventListener("keydown", (e) => {
		if (e.key !== "Tab") return;
		const items = focusables();
		if (items.length === 0) return;
		const first = items[0];
		const last = items[items.length - 1];
		const active = document.activeElement as HTMLElement | null;
		if (e.shiftKey && (active === first || !overlay.contains(active))) {
			e.preventDefault();
			last.focus();
		} else if (!e.shiftKey && active === last) {
			e.preventDefault();
			first.focus();
		}
	});

	return { open, close, isOpen: () => opened };
}
